import { DataManager } from "../../../../../eventfive.awi.mosaic-pwa/theme/ts/awi/model/DataManager.js";
import { VOGraph, VOGraphValue } from "../../../../../eventfive.awi.mosaic-pwa/theme/ts/awi/model/DataStructure.js";
import { SensorGetMode } from "../../../../../eventfive.awi.mosaic-pwa/theme/ts/awi/model/Sensor.js";
import { DateUtil } from "../../../../../eventfive.awi.mosaic-pwa/theme/ts/awi/utils/DateUtil.js";
import { Language } from "../../../../../eventfive.awi.mosaic-pwa/theme/ts/awi/model/Language.js";
import { VOConfig } from "../model/DataStructure.js";

export type DiagramGraph = "temperature" | "ice-extent";

export class Diagram {
    public onChange = e5.Signal.create<[VOGraphValue]>();
    public onResize = e5.Resize.create();

    public element: HTMLDivElement;

    private _start: Date;
    private _end: Date;
    private _monthCount: number = 0;
    private _grid: HTMLCanvasElement;
    private _graph: HTMLCanvasElement;
    private _yAxis: HTMLCanvasElement;
    private _xAxis: HTMLCanvasElement;
    private _content: HTMLDivElement;
    private _labelWidth: number = 36;
    private _xAxisMarginTop: number = 10;
    private _graphs: Graph[] = [];
    private _graphList: Graph[] = [];
    private _marker: HTMLDivElement;
    private _value: VOGraphValue = null;
    private _date: Date = new Date();

    constructor(private _data: DataManager, private _config:VOConfig) {
        this.element = document.createElement("div");
        this.element.classList.add("diagram");
        this.element.innerHTML = `
        <div class="toolbar"></div>
        <canvas class="y-axis"></canvas>
        <div class="content">
            <canvas class="grid"></canvas>
            <canvas class="graph"></canvas>
            <canvas class="x-axis"></canvas>
            <div class="marker">
                <div class="arrow left icon-125"></div>
                <div class="arrow right icon-124"></div>
                <div class="info">
                    <div class="info-item info-date"></div>
                    <div class="info-item info-value"></div>
                    <div class="info-line"></div>
                </div>
            </div>
        </div>
        `;
        this._yAxis = this.element.querySelector(".y-axis");
        this._content = this.element.querySelector(".content");
        this._grid = this.element.querySelector(".grid");
        this._graph = this.element.querySelector(".graph");
        this._xAxis = this.element.querySelector(".x-axis");
        this._marker = this.element.querySelector(".marker");

        //Set first date of month.
        this._start = new Date(_data.config.start);
        this._start.setUTCDate(1);

        //Check for end date.
        //If no date is given, use fallback.
        // var end = _data.config.end;
        // if (!end) {
        //     console.warn("The end date of the MOSAiC expedition is not set!. Fallback to 14. October 2020");
        //     end = DateUtil.fromString("2020-10-14");
        //     _data.config.end = end;
        // }
        // this._end = new Date(end);

        // this._end = DateUtil.fromString("2020-10-14");
        this._end = this._data.config.end;

        //Count the months between start and end (ceil).
        this._monthCount = DateUtil.monthDiff(this._start, this._end) + 2;

        //Temperature graph
        var temperatureGraph: Graph = {
            color: "rgba(255, 255, 255, 0.6)",
            values: [],
            vStart: 40,
            vEnd: -60,
            vStepSize: 10,
            title_de: Language.translate("main.graph-temperature", "de"),
            title_en: Language.translate("main.graph-temperature", "en"),
            key: "temperature",
            format: (value: number) => value.toFixed(1).replace(".", ",") + " °C"
        };

        var mode: SensorGetMode = "nearest";
        var entries = this._data.sensorMosaic.getEntryForEachDay(mode);
        for (var i = 0; i < entries.length; ++i) {
            var date = new Date(entries[i].date);
            if (DateUtil.smaller(date, this._data.config.start) || DateUtil.greater(date, this._end))
                continue;
            temperatureGraph.values.push(
                {
                    date: date,
                    value: entries[i].temperature,
                }
            );
        }
        this._graphList.push(temperatureGraph);

        //Ice extent graph
        var iceExtentGraph: Graph = {
            color: "rgba(255, 255, 255, 0.6)",
            values: [],
            vStart: 20,
            vEnd: 2,
            vStepSize: 1,
            title_de: Language.translate("main.graph-ice-extent", "de"),
            title_en: Language.translate("main.graph-ice-extent", "en"),
            key: "ice-extent",
            format: (value: number) => (value).toFixed(1).replace(".", ",") + " mio km²",
            showYAxisLabel: (value: number) => (value % 2) === 0
        };
        var iceExtents = this._data.data.ice_extent;
        for (var iceExtent of iceExtents) {
            var date = new Date(iceExtent.date);
            if (DateUtil.smaller(date, this._data.config.start) || DateUtil.greater(date, this._end))
                continue;
            iceExtentGraph.values.push(
                {
                    date: date,
                    value: iceExtent.value / 1000000
                }
            );
        }
        this._graphList.push(iceExtentGraph);

        //Set current graph/s
        // this._graphs = [temperatureGraph];
        this.setGraph(this._config.initial_logbook_diagram);

        //Add a tab for each graph to the toolbar
        var toolbar = ``;
        for (var temperatureGraph of this._graphList) {
            toolbar += `
            <span class="toolbar-item ${this._graphs.indexOf(temperatureGraph) !== -1 ? "selected" : ""}" data-graphkey="${temperatureGraph.key}">${Language.trans(temperatureGraph.title_de, temperatureGraph.title_en)}</span>`;
        }
        this.element.querySelector(".toolbar").innerHTML = toolbar;

        /**
         * Event handling
         */
        this.onResize = e5.Resize.create(this.element);
        this.onResize.add(() => this.resize());

        var actor = new e5.Actor(this._content, {
            maxPointers: 1
        });

        var updateMarker = () => {
            var local = e5.Transform.toLocal(this._content, actor.pointer.x, actor.pointer.y);
            local.x = Math.round(e5.Calc.clamp(local.x, this.minX, this.maxX));
            var value = this.getValue(local.x);
            if (value) {
                this.onChange.dispatch(value);
            } else {
                console.log("Could not get value from position:", local.x);
            }
        };

        actor.onDown.add(() => {
            this._marker.classList.add("dragging");
            updateMarker();
        });

        actor.onMove.add(updateMarker);

        actor.onUp.add(() => {
            if (actor.pointers.length === 0)
                this._marker.classList.remove("dragging");
        });

        $(this.element).on("click", "[data-graphkey]:not(.selected)", (e) => {
            var key = e.currentTarget.getAttribute("data-graphkey") as any;
            this.setGraph(key);
        });
    }

    /**
     * Sets a graph by the given key.
     * 
     * @param key The key of the graph.
     */
    private setGraph(key: DiagramGraph): void {
        var g = e5.array.match(this._graphList, "key", key);
        if (!g) {
            console.warn("Could not find graph by key:", key);
            return;
        }
        this._graphs = [g];
        this.resize();
        
        //Deselect all selected (should be a single one) graph tabs
        $("[data-graphkey].selected", this.element).removeClass("selected");
        
        //Select current graph tab
        $("[data-graphkey='" + key + "']", this.element).addClass("selected");

        this.setDate(this._date, true);
    }

    /**
     * Sets the given date.
     * 
     * @param date The date to apply.
     * @param dispatchChange Should the onChange signal dispatched.
     */
    public setDate(date: Date, dispatchChange: boolean = true): void {
        this._date = new Date(date);
        date = DateUtil.clamp(date, this._start, this._end);
        date = DateUtil.round(date);
        var x = this.getX(date);
        x = e5.Calc.clamp(x, this.minX, this.maxX);
        var value = this.getValue(x);
        if (!value) {
            console.warn("Could not get value by date:", date);
            return;
        }
        var y = Math.round(this.getY(value.value));        

        //Update marker position
        this._marker.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px)`;

        //Update marker left/right arrows
        this._marker.querySelector(".left").classList.toggle("hidden", DateUtil.smallerEqual(date, this.minDate));
        this._marker.querySelector(".right").classList.toggle("hidden", DateUtil.greaterEqual(date, this.maxDate));

        /**
         * Update marker info-label
         */

        var bottom = e5.Calc.clampMax(50, y - 20);

        //Update position
        this._marker.querySelector<HTMLDivElement>(".info").style.bottom = bottom + "px";

        //Update line height
        this._marker.querySelector<HTMLDivElement>(".info>.info-line").style.height = e5.Calc.clampMin(bottom - 22, 0) + "px";

        //Update label value (e.g.: 4,3 mio km2)
        this._marker.querySelector(".info>.info-value").textContent = this.current.format(value.value);

        //Update label date (e.g.: 30. Sep 2019)
        this._marker.querySelector(".info>.info-date").textContent = DateUtil.toStringReadableShort(date, Language.current);

        this._value = value;
        if (dispatchChange)
            this.onChange.dispatch(value);
    }

    /**
     * Returns the current graph value.
     */
    public get value(): VOGraphValue {
        return this._value;
    }

    /**
     * Resizes all elements of the diagram.
     */
    public resize(): void {
        this.drawGrid();
        this.drawXAxis();
        this.drawYAxis();
        this.drawGraph();
        if (this._date)
            this.setDate(this._date);
    }

    /**
     * Returns the vertical (y-axis) step size.
     */
    private get vStepSize(): number {
        return this.current ? this.current.vStepSize : 10;
    }

    /**
     * Returns the vertical (y-axis) end value.
     */
    private get vEnd(): number {
        return this.current ? this.current.vEnd : -10;
    }

    /**
     * Returns the vertical (y-axis) start value.
     */
    private get vStart(): number {
        return this.current ? this.current.vStart : 10;
    }

    /**
     * Draws the current graph.
     */
    private drawGraph(): void {
        this._graph.width = this._graph.offsetWidth;
        this._graph.height = this._graph.offsetHeight;

        var ctx = this._graph.getContext("2d");
        ctx.clearRect(0, 0, this._graph.width, this._graph.height);

        if (!this.current)
            return;

        ctx.fillStyle = "none";
        ctx.lineWidth = 1;
        ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
        ctx.shadowBlur = 6;

        for (var g of this._graphs) {
            ctx.strokeStyle = g.color;
            ctx.beginPath();
            for (var i = 0; i < g.values.length; ++i) {
                var x = this.getX(g.values[i].date);
                var y = this.getY(g.values[i].value);

                g.values[i].x = Math.round(x);
                g.values[i].y = Math.round(y);

                if (i === 0) {
                    ctx.moveTo(x, y);
                }
                else {
                    ctx.lineTo(x, y);
                }
            }
            ctx.stroke();
        }
    }

    /**
     * Returns the current graph.
     */
    private get current(): Graph {
        return this._graphs.length > 0 ? this._graphs[0] : null;
    }

    /**
     * Draws the grid (horizontal lines) for the current graph.
     */
    private drawGrid(): void {
        this._grid.width = this._grid.offsetWidth;
        this._grid.height = this._grid.offsetHeight;

        var ctx = this._grid.getContext("2d");
        ctx.clearRect(0, 0, this._grid.width, this._grid.height);

        ctx.fillStyle = "none";
        ctx.strokeStyle = "rgba(255, 255, 255, 0.05)";
        ctx.lineWidth = 1;

        var linesV = Math.abs(Math.ceil((this.vEnd - this.vStart) / this.vStepSize));
        var w = this._grid.width;
        var h = this._grid.height - this._xAxisMarginTop;
        var stepV = h / linesV;
        for (var i = 0; i <= linesV; ++i) {
            var y = Math.round(i * stepV) + 0.5;
            ctx.beginPath();
            ctx.moveTo(0.5, y);
            ctx.lineTo(Math.round(w) - 0.5, y);
            ctx.stroke();
        }
    }

    /**
     * Draws the x-axis for the current graph.
     */
    private drawXAxis(): void {
        this._xAxis.width = this._xAxis.offsetWidth;
        this._xAxis.height = this._xAxis.offsetHeight;

        var start = new Date(this._start);

        var ctx = this._xAxis.getContext("2d");
        ctx.clearRect(0, 0, this._xAxis.width, this._xAxis.height);
        ctx.font = "12px Regular";
        ctx.fillStyle = "#FFF";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        var yPos = this._xAxis.height - 22.5;
        var positions: number[] = [];
        for (var i = 0; i < this._monthCount; ++i) {
            var monthLabel = e5.Text.month(start.getMonth(), Language.current).slice(0, 3);

            var xPos = this.getX(start);

            //Month label
            ctx.font = "12px Regular";
            ctx.fillStyle = "#FFF";
            ctx.fillText(monthLabel, xPos, yPos);

            //Year label
            ctx.font = "10px Regular";
            ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
            ctx.fillText(start.getFullYear() + "", xPos, yPos + 15);

            positions.push(xPos);

            start.setUTCMonth(start.getUTCMonth() + 1);
        }

        //Lines between months
        var off = this._labelWidth * 0.5;
        ctx.fillStyle = null;
        ctx.strokeStyle = "rgba(255, 255, 255, 0.2)";
        for (var i = 1; i < positions.length; ++i) {
            var a = positions[i - 1] + off;
            var b = positions[i] - off;
            ctx.moveTo(a, yPos);
            ctx.lineTo(b, yPos);
        }
        ctx.stroke();
    }

    /**
     * Draws the y-axis for the current graph.
     */
    private drawYAxis(): void {
        this._yAxis.width = this._yAxis.offsetWidth;
        this._yAxis.height = this._yAxis.offsetHeight;

        var ctx = this._yAxis.getContext("2d");
        ctx.clearRect(0, 0, this._yAxis.width, this._yAxis.height);

        ctx.font = "11px Light";
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "rgba(255, 255, 255, 0.2)";

        var linesV = Math.abs(Math.ceil((this.vEnd - this.vStart) / this.vStepSize));
        var h = this._grid.height - this._xAxisMarginTop;
        var stepV = h / linesV;
        var offsetY = this._content.offsetTop;
        for (var i = 0; i <= linesV; ++i) {
            var y = Math.round(i * stepV + offsetY) + 0.5;
            var s = i * this.vStepSize;
            if (this.vStart > this.vEnd) {
                s *= -1;
            }
            var t = this.vStart + s;

            if (this.current.showYAxisLabel === undefined || this.current.showYAxisLabel(t))
                ctx.fillText(t + "", this._yAxis.width - 14, y);
        }
    }

    /**
     * Returns the local x position for the given date.
     * 
     * @param date Any date.
     */
    private getX(date: Date): number {
        var margin = this._labelWidth * 0.5;
        var sizePerMonth = (this._xAxis.offsetWidth - margin * 2) / (this._monthCount - 1);
        var monthDiff = DateUtil.monthDiff(this._start, date);
        var xPos = monthDiff * sizePerMonth;
        xPos += margin;
        var ratioInMonth = (date.getUTCDate() - 1) / DateUtil.daysOfMonth(date);
        xPos += ratioInMonth * sizePerMonth;
        return xPos;
    }

    /**
     * Returns the local y position for given graph value.
     */
    private getY(value: number): number {
        var diff = this.vStart - this.vEnd;
        var linesV = Math.abs(Math.ceil((this.vEnd - this.vStart) / this.vStepSize));
        var stepV = (this._grid.height - (this._xAxisMarginTop)) / (linesV);
        var h = Math.round(linesV * stepV);
        var valueRatio = (value - this.vEnd) / diff;
        return (h - valueRatio * h);
    }

    /**
     * Returns the min local x position.
     */
    private get minX(): number {
        if (this.current && this.current.values.length > 0) {
            return Math.round(Math.max(this._labelWidth * 0.5, this.getX(this.current.values[0].date)));
        }
        return this._labelWidth * 0.5;
    }

    /**
     * Returns the max local x position.
     */
    private get maxX(): number {
        if (this.current && this.current.values.length > 0) {
            return Math.round(Math.min(this._content.offsetWidth - this._labelWidth * 0.5, this.getX(this.current.values[this.current.values.length - 1].date)));
        }
        return this._content.offsetWidth - this._labelWidth * 0.5;
    }

    /**
     * Returns the min date for the current Graph.
     * If no graph is defined, it returns the start date given by PWA config.
     */
    private get minDate(): Date {
        var date: Date;
        if (this.current && this.current.values.length > 0) {
            date = this.current.values[0].date;
        } else {
            date = this._start;
        }
        return DateUtil.clamp(date, this._data.config.start, this._end);
    }

    /**
     * Returns the max date for the current Graph.
     * If no graph is defined, it returns the end date given by PWA config.
     */
    private get maxDate(): Date {
        var date: Date;
        if (this.current && this.current.values.length > 0) {
            date = this.current.values[this.current.values.length - 1].date;
        }
        else {
            date = this._end;
        }
        return DateUtil.clamp(date, this._data.config.start, this._end);
    }

    /**
     * Gets the graph value for the given local x position.
     * 
     * @param xPos The local x position.
     */
    private getValue(xPos: number): VOGraphValue {
        xPos = Math.round(xPos);
        var diff = Number.MAX_VALUE;
        var target: VOGraphValue = null;
        if (this.current && this.current.values.length > 0) {
            for (var value of this.current.values) {
                var d = Math.abs(value.x - xPos);
                if (d < diff) {
                    diff = d;
                    target = value;
                }
            }
        }
        return target;
    }

    public reset():void{
        this.setGraph("temperature");
    }
}

interface Graph extends VOGraph {
    vStepSize: number;
    vStart: number;
    vEnd: number;
    key: string;
    format: (value: number) => string;
    showYAxisLabel?: (value: number) => boolean;
}